import { Form } from "react-final-form";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import arrayMutators from "final-form-arrays";
import { RecipeScalingApiEndpoints } from "api/api-endpoints";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import BioreactorImage from "pages/PS-Workspace/components/BioreactorImage";
import Variants from "./components/Variants";
import EntityDialog from "rs-components/EntityDialog";
import LearnMoreLink from "rs-components/LearnMoreLink";
import { HELP_SYSTEM_URL, HelpId } from "shared/helpSystemBindings";
import { cellModelOptionsAvailableForSimulationSelector, fetchingSimulationPreviewDataSelector, recipeOptionsAvailableForSimulationSelector, simulationDefaultNameSelector, simulationDialogInitialValuesSelector, simulationPreviewDataSelector, simulationPreviewErroredSelector, simulationSocketErrorSelector, workspaceCellModelsSelector, workspaceCurrentSimulationIdSelector, workspaceCurrentSimulationSelector, workspaceCurrentVariantIdSelector, workspaceRecipesSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { addSimulation, cancelOutdatedPreview, editSimulation, getSimulationPreview, persistSimulationPreview, setCurrentSimulation, setCurrentVariantId, setSimulationDialogOpen, } from "store/rs-workspace/actions/rs-workspace.actions";
import { getBioreactorDefaultImpellerOption, getBioreactorDefaultSpargerOption, } from "store/global/global.utils";
import { GetRestrictionsMethod, RequestTemplate } from "store/knowledge-base-item/knowledge-base-item.types";
import { isEditableSelector, restrictionsSelector, } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { CancelOutdatedPreviewType } from "store/rs-workspace/rs-workspace.types";
import { getRestrictions } from "store/knowledge-base-item/knowledge-base-item.actions";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { bioreactorsListSelector, bioreactorsOptionsSelector, impellerOptionsSelector, spargerOptionsSelector, } from "store/global/global.selectors";
import { isSimulationCompleted, isSimulationVariantCompleted, } from "store/rs-workspace/utils/completeness.utils";
import { RsWorkspaceScenarioLabels } from "shared/labels";
import { RsWorkspaceEntityType } from "shared/types";
import { someFormFieldModified } from "shared/validators";
import * as styles from "./styles.module.scss";
export var SimulationFormFields;
(function (SimulationFormFields) {
    SimulationFormFields["name"] = "name";
    SimulationFormFields["cellModel"] = "cellModel";
    SimulationFormFields["recipe"] = "recipe";
    SimulationFormFields["bioreactor"] = "bioreactor";
    SimulationFormFields["sparger"] = "sparger";
    SimulationFormFields["impeller"] = "impeller";
    SimulationFormFields["variants"] = "variants";
    SimulationFormFields["flowConfig"] = "flowConfig";
    SimulationFormFields["stepsConfig"] = "stepsConfig";
    SimulationFormFields["stepId"] = "stepId";
    SimulationFormFields["flowId"] = "flowId";
    SimulationFormFields["recipeParameters"] = "recipeParameters";
    SimulationFormFields["cellModelParameters"] = "cellModelParameters";
    SimulationFormFields["parameter"] = "parameter";
    SimulationFormFields["type"] = "type";
    SimulationFormFields["id"] = "id";
    SimulationFormFields["_selectedParameterId"] = "_selectedParameterId";
    SimulationFormFields["parameterId"] = "parameterId";
    SimulationFormFields["value"] = "value";
    SimulationFormFields["mean"] = "mean";
    SimulationFormFields["sd"] = "sd";
    SimulationFormFields["from"] = "from";
    SimulationFormFields["to"] = "to";
    SimulationFormFields["notSaved"] = "notSaved";
    SimulationFormFields["_adjustmentId"] = "_adjustmentId";
    SimulationFormFields["variables"] = "variables";
})(SimulationFormFields || (SimulationFormFields = {}));
export var VariantSourceType;
(function (VariantSourceType) {
    VariantSourceType["Recipe"] = "Recipe";
    VariantSourceType["CellModel"] = "CellModel";
    VariantSourceType["AlternativeFlow"] = "AlternativeFlow";
})(VariantSourceType || (VariantSourceType = {}));
const getRestrictionsPayload = (post) => ({
    rsEndpoint: RecipeScalingApiEndpoints.workspaces,
    template: post ? RequestTemplate.SIMULATION_POST : RequestTemplate.SIMULATION_PUT,
    method: post ? GetRestrictionsMethod.POST : GetRestrictionsMethod.PUT,
});
const SimulationDialog = () => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [bioreactorId, setBioreactorId] = useState(undefined);
    const { lines = [] } = useSelector(simulationPreviewDataSelector);
    const currentSimulationId = useSelector(workspaceCurrentSimulationIdSelector);
    const currentSimulation = useSelector(workspaceCurrentSimulationSelector);
    const currentVariantId = useSelector(workspaceCurrentVariantIdSelector);
    const fetchingGraphData = useSelector(fetchingSimulationPreviewDataSelector(currentSimulation?.id ?? -1));
    const simulationErrored = useSelector(simulationPreviewErroredSelector(currentSimulationId, false));
    const simulationDefaultName = useSelector(simulationDefaultNameSelector);
    const initialValues = useSelector(simulationDialogInitialValuesSelector);
    const editable = useSelector(isEditableSelector);
    const restrictions = useSelector(restrictionsSelector(getRestrictionsPayload(currentSimulationId === null)));
    const recipesOptions = useSelector(recipeOptionsAvailableForSimulationSelector(currentSimulation?.recipeId));
    const cellModelsOptions = useSelector(cellModelOptionsAvailableForSimulationSelector(currentSimulation?.cellModelId));
    const bioreactorOptions = useSelector(bioreactorsOptionsSelector);
    const spargerOptions = useSelector(spargerOptionsSelector(bioreactorId));
    const bioreactorsList = useSelector(bioreactorsListSelector);
    const impellerOptions = useSelector(impellerOptionsSelector(bioreactorId));
    const cellModels = useSelector(workspaceCellModelsSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const socketError = useSelector(simulationSocketErrorSelector);
    const triggerPreviewCalculation = useCallback((currentValues, updatedFields, recalculateAllVariants) => {
        const updatedValues = { ...currentValues, ...updatedFields };
        const currentVariant = updatedValues?.variants?.find(({ id }) => id === updatedValues[SimulationFormFields._adjustmentId]);
        const hasAllValues = [
            updatedValues[SimulationFormFields.bioreactor],
            updatedValues[SimulationFormFields.cellModel],
            updatedValues[SimulationFormFields.recipe],
            updatedValues[SimulationFormFields.sparger],
            updatedValues[SimulationFormFields.impeller],
            updatedValues[SimulationFormFields._adjustmentId],
        ].every((formValue) => formValue !== null);
        if (updatedValues[SimulationFormFields._adjustmentId] !== 0 &&
            !isSimulationVariantCompleted({
                id: currentSimulation?.id ?? -1,
                name: currentSimulation?.name ?? "",
                tags: currentSimulation?.tags ?? [],
                cellModelId: updatedValues[SimulationFormFields.cellModel]?.value,
                recipeId: updatedValues[SimulationFormFields.recipe]?.value,
                state: {
                    bioreactorId: updatedValues[SimulationFormFields.bioreactor]?.value ?? null,
                    impellerId: updatedValues[SimulationFormFields.impeller]?.value ?? null,
                    spargerId: updatedValues[SimulationFormFields.sparger]?.value ?? null,
                    adjustments: [],
                },
            }, cellModels, recipes, currentVariant)) {
            return;
        }
        if (!hasAllValues || fetchingGraphData) {
            return;
        }
        // TODO: do not trigger calculation if form/variant didn't change
        let variantsToRecalculate = [currentVariant ?? undefined];
        if (recalculateAllVariants) {
            variantsToRecalculate = [undefined, ...updatedValues?.variants];
        }
        for (const variant of variantsToRecalculate) {
            dispatch(getSimulationPreview({
                simulationId: currentSimulationId === null ? -1 : currentSimulationId,
                cellModelId: updatedValues[SimulationFormFields.cellModel]?.value,
                recipeId: updatedValues[SimulationFormFields.recipe]?.value,
                bioreactorId: updatedValues[SimulationFormFields.bioreactor]?.value,
                spargerId: updatedValues[SimulationFormFields.sparger]?.value,
                impellerId: updatedValues[SimulationFormFields.impeller]?.value,
                adjustment: variant,
            }));
        }
    }, [fetchingGraphData, dispatch, currentSimulationId, cellModels, recipes, currentSimulation]);
    const resetPreview = useCallback(() => dispatch(cancelOutdatedPreview({
        previewType: CancelOutdatedPreviewType.Simulation,
        elementId: currentSimulationId ?? -1,
    })), [currentSimulationId, dispatch]);
    const onDialogClose = useCallback((form) => {
        dispatch(setSimulationDialogOpen(false));
        dispatch(setCurrentSimulation(null));
        dispatch(setCurrentVariantId(0));
        form.change(SimulationFormFields.name, null);
    }, [dispatch]);
    const onUpsertSimulation = useCallback((values, form) => {
        const formRenderProps = form.getState();
        const formModified = someFormFieldModified(formRenderProps, [SimulationFormFields.variables]);
        if (!formModified && currentSimulationId !== null) {
            onDialogClose(form);
            if (currentSimulationId !== null) {
                dispatch(persistSimulationPreview(currentSimulationId));
            }
            return;
        }
        const payload = {
            workspaceId: parseInt(id),
            name: values[SimulationFormFields.name] ?? simulationDefaultName,
            recipeId: values[SimulationFormFields.recipe]?.value,
            cellModelId: values[SimulationFormFields.cellModel]?.value,
            state: {
                bioreactorId: values[SimulationFormFields.bioreactor]?.value,
                impellerId: values[SimulationFormFields.impeller]?.value,
                spargerId: values[SimulationFormFields.sparger]?.value,
                adjustments: values[SimulationFormFields.variants] ?? null,
            },
        };
        if (currentSimulationId !== null) {
            dispatch(editSimulation({
                ...payload,
                simulationId: currentSimulationId,
            }));
        }
        else {
            dispatch(addSimulation(payload));
        }
    }, [dispatch, id, currentSimulationId, simulationDefaultName, onDialogClose]);
    const onBioreactorChange = useCallback((option, form, values) => {
        const defaultSparger = getBioreactorDefaultSpargerOption(bioreactorsList, option.value);
        const defaultImpeller = getBioreactorDefaultImpellerOption(bioreactorsList, option.value);
        setBioreactorId(option.value);
        form.change(SimulationFormFields.sparger, defaultSparger);
        form.change(SimulationFormFields.impeller, defaultImpeller);
        triggerPreviewCalculation(values, {
            [SimulationFormFields.bioreactor]: option,
            [SimulationFormFields.sparger]: defaultSparger,
            [SimulationFormFields.impeller]: defaultImpeller,
        }, true);
    }, [bioreactorsList, triggerPreviewCalculation]);
    const getValidationSocketErrorMessage = useCallback((propertyName) => socketError?.errors.find((error) => error.propertyName === propertyName)?.text, [socketError]);
    const checkCompleteStatus = useCallback((values) => isSimulationCompleted({
        id: currentSimulation?.id ?? -1,
        name: currentSimulation?.name ?? "",
        tags: currentSimulation?.tags ?? [],
        recipeId: values[SimulationFormFields.recipe]?.value,
        cellModelId: values[SimulationFormFields.cellModel]?.value,
        state: {
            bioreactorId: values[SimulationFormFields.bioreactor]?.value ?? null,
            impellerId: values[SimulationFormFields.impeller]?.value ?? null,
            spargerId: values[SimulationFormFields.sparger]?.value ?? null,
            adjustments: values[SimulationFormFields.variants] ?? null,
        },
    }, cellModels, recipes), [cellModels, currentSimulation?.id, currentSimulation?.name, currentSimulation?.tags, recipes]);
    const onVariantChange = useCallback((values, variantId) => {
        if (variantId === currentVariantId)
            return;
        triggerPreviewCalculation(values, { [SimulationFormFields._adjustmentId]: variantId });
        dispatch(setCurrentVariantId(variantId));
    }, [dispatch, triggerPreviewCalculation, currentVariantId]);
    const onCellModelChange = useCallback((form, values, option) => {
        const updatedVariants = (values?.[SimulationFormFields.variants] ?? [])?.map((variant) => ({
            ...variant,
            cellModelParameters: [],
        }));
        form.change(SimulationFormFields.variants, updatedVariants);
        triggerPreviewCalculation(values, {
            [SimulationFormFields.cellModel]: option,
            [SimulationFormFields.variants]: updatedVariants,
        }, true);
    }, [triggerPreviewCalculation]);
    const onRecipeChange = useCallback((form, values, option) => {
        const updatedVariants = (values?.[SimulationFormFields.variants] ?? [])?.map((variant) => ({
            ...variant,
            recipeParameters: [],
            flowConfig: null,
        }));
        form.change(SimulationFormFields.variants, updatedVariants);
        triggerPreviewCalculation(values, {
            [SimulationFormFields.recipe]: option,
            [SimulationFormFields.variants]: updatedVariants,
        }, true);
    }, [triggerPreviewCalculation]);
    const onDialogOpened = useCallback(() => {
        if (currentSimulationId !== null && lines?.length === 0 && checkCompleteStatus(initialValues)) {
            triggerPreviewCalculation(initialValues, {});
        }
    }, [currentSimulationId, lines?.length, checkCompleteStatus, initialValues, triggerPreviewCalculation]);
    const getCellModelErrorMessage = useCallback((cellModel) => {
        if (cellModel !== null && !cellModels.find(({ id }) => cellModel?.value === id)?.isCompleted) {
            return RsWorkspaceScenarioLabels.INCOMPLETE_ERROR_MESSAGE.replace("#TYPE", "Cell Model");
        }
        return getValidationSocketErrorMessage("CellModelId");
    }, [cellModels, getValidationSocketErrorMessage]);
    const getRecipeErrorMessage = useCallback((recipe) => {
        if (recipe !== null && !recipes.find(({ id }) => recipe?.value === id)?.isCompleted) {
            return RsWorkspaceScenarioLabels.INCOMPLETE_ERROR_MESSAGE.replace("#TYPE", "Recipe");
        }
        return getValidationSocketErrorMessage("RecipeId");
    }, [getValidationSocketErrorMessage, recipes]);
    useEffect(() => {
        if (!bioreactorId && initialValues[SimulationFormFields.bioreactor] !== null) {
            setBioreactorId(initialValues[SimulationFormFields.bioreactor]?.value);
        }
    }, [bioreactorId, initialValues]);
    useEffect(() => {
        !restrictions && dispatch(getRestrictions(getRestrictionsPayload(currentSimulationId === null)));
    }, [restrictions, dispatch, currentSimulationId]);
    return (React.createElement(Form, { onSubmit: onUpsertSimulation, initialValues: initialValues, mutators: { ...arrayMutators }, keepDirtyOnReinitialize: true, render: ({ handleSubmit, form, values, ...renderProps }) => (React.createElement(EntityDialog, { nameFieldName: SimulationFormFields.name, nameFieldRestrictions: getFieldRestrictions(restrictions, [
                getRequestName(SimulationFormFields.name),
            ]), entityType: RsWorkspaceEntityType.Simulation, isOpen: true, onDialogClose: () => onDialogClose(form), onClosing: async () => await form.reset(), onCancel: resetPreview, onDialogOpened: onDialogOpened, entityCompleted: checkCompleteStatus(values), entityErrored: simulationErrored, handleSubmit: handleSubmit, formRenderProps: renderProps, selectedId: currentSimulationId, fieldsNotTrackedForChange: [SimulationFormFields.variables] },
            React.createElement("div", { className: styles.body },
                React.createElement("div", { className: styles.inputs },
                    React.createElement("h4", { className: styles.subtitle }, "Inputs"),
                    React.createElement("div", { className: styles.fields },
                        React.createElement("div", { className: styles.field },
                            React.createElement(Input, { name: SimulationFormFields.cellModel, component: SelectInput, label: "Cell Model", items: cellModelsOptions, placeholder: "Select cell model", itemRenderer: SelectItemRenderer, restrictions: getFieldRestrictions(restrictions, [getRequestName("CellModelId")]), externalError: getCellModelErrorMessage(values[SimulationFormFields.cellModel]), onChange: (option) => onCellModelChange(form, values, option), readonly: !editable, autoFocus: true, classes: {
                                    component: styles.selectWrapper,
                                    label: styles.title,
                                } })),
                        React.createElement("div", { className: styles.field },
                            React.createElement(Input, { name: SimulationFormFields.recipe, component: SelectInput, label: "Recipe", items: recipesOptions, placeholder: "Select recipe", itemRenderer: SelectItemRenderer, restrictions: getFieldRestrictions(restrictions, [getRequestName("RecipeId")]), onChange: (option) => onRecipeChange(form, values, option), externalError: getRecipeErrorMessage(values[SimulationFormFields.recipe]), readonly: !editable, classes: {
                                    component: styles.selectWrapper,
                                    label: styles.title,
                                } })),
                        React.createElement("h4", { className: styles.subtitle }, "Bioreactor Configuration"),
                        React.createElement("div", { className: styles.field },
                            React.createElement(Input, { name: SimulationFormFields.bioreactor, component: SelectInput, label: "Bioreactor", items: bioreactorOptions.slice(1, bioreactorOptions.length), placeholder: "Select bioreactor", itemRenderer: SelectItemRenderer, restrictions: getFieldRestrictions(restrictions, [getRequestName("State.BioreactorId")]), onChange: (option) => onBioreactorChange(option, form, values), readonly: !editable, classes: {
                                    component: styles.selectWrapper,
                                    label: styles.title,
                                } })),
                        values[SimulationFormFields.bioreactor] !== null && (React.createElement("div", { className: styles.combinedFields },
                            React.createElement("div", { className: styles.left },
                                React.createElement("div", { className: styles.field },
                                    React.createElement(Input, { component: SelectInput, items: spargerOptions, name: SimulationFormFields.sparger, placeholder: "Select", label: "Sparger", itemRenderer: SelectItemRenderer, restrictions: getFieldRestrictions(restrictions, [
                                            getRequestName("State.SpargerId"),
                                        ]), onChange: (value) => {
                                            triggerPreviewCalculation(values, { [SimulationFormFields.sparger]: value }, true);
                                        }, readonly: !editable, classes: {
                                            component: styles.selectWrapper,
                                            label: styles.title,
                                        } })),
                                React.createElement("div", { className: styles.field },
                                    React.createElement(Input, { component: SelectInput, items: impellerOptions, name: SimulationFormFields.impeller, placeholder: "Select", label: "Impeller", itemRenderer: SelectItemRenderer, restrictions: getFieldRestrictions(restrictions, [
                                            getRequestName("State.ImpellerId"),
                                        ]), onChange: (value) => {
                                            triggerPreviewCalculation(values, { [SimulationFormFields.impeller]: value }, true);
                                        }, readonly: !editable, classes: {
                                            component: styles.selectWrapper,
                                            label: styles.title,
                                        } }))),
                            React.createElement("div", { className: styles.right },
                                React.createElement(BioreactorImage, { bioreactorId: values[SimulationFormFields.bioreactor]?.value, size: 98 }))))),
                    React.createElement(LearnMoreLink, { url: `${HELP_SYSTEM_URL}#cshid=${HelpId.SIMULATIONS}` })),
                React.createElement(Variants, { onVariantChange: (variantId) => onVariantChange(values, variantId), triggerPreviewCalculation: (updatedFields) => triggerPreviewCalculation(values, updatedFields), restrictions: getFieldRestrictions(restrictions, [getRequestName("State.Adjustments")]) })))) }));
};
export default SimulationDialog;
